import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Banknote, Smartphone, CreditCard, Users, ArrowDownCircle, ArrowUpCircle,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Wallet, ChevronDown,
  ChevronRight, Clock, XCircle, RefreshCw, FileText
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * UNIFIED DRAWER RECONCILIATION TAB
 * 
 * Displays complete financial reconciliation for:
 * - CASH (with opening balance, expected/actual closing, variance)
 * - UPI (daily movement only)
 * - CARD (daily movement only)
 * - CUSTOMER CREDIT (receivables movement)
 * 
 * Every total is drill-downable to underlying transactions.
 * Reuses existing /api/shop-reconciliation/* APIs.
 */

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatTime = (isoString) => {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

// Transaction List Component with Enhanced Drill-Down Traceability
function TransactionList({ entries, showInvoice = true, showTraceability = false }) {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!entries || entries.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic px-2">No transactions</div>
    );
  }

  // Check if any entries have traceability details
  const hasTraceability = entries.some(e => e.has_credit_adjustment || e.other_payments?.length > 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger 
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
        data-testid="transaction-list-toggle"
      >
        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        View {entries.length} transaction{entries.length !== 1 ? "s" : ""}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="max-h-64 overflow-auto border rounded text-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs py-1">Time</TableHead>
                {showInvoice && <TableHead className="text-xs py-1">Ref</TableHead>}
                <TableHead className="text-xs py-1">Customer</TableHead>
                {(showTraceability || hasTraceability) && (
                  <TableHead className="text-xs py-1">Breakdown</TableHead>
                )}
                <TableHead className="text-xs py-1 text-right">Drawer Impact</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry, idx) => {
                const hasCreditAdj = entry.has_credit_adjustment || entry.customer_credit > 0;
                const hasOtherPay = entry.other_payments?.length > 0;
                const showBreakdown = (showTraceability || hasTraceability) && (hasCreditAdj || hasOtherPay || entry.sale_total !== entry.amount);
                
                return (
                  <TableRow 
                    key={entry.source_id || idx} 
                    className={entry.voided ? "bg-red-50 line-through" : hasCreditAdj ? "bg-amber-50/50" : ""}
                    data-testid={`transaction-row-${entry.source_id || idx}`}
                  >
                    <TableCell className="py-1.5 font-mono text-[10px] align-top">
                      {formatTime(entry.timestamp)}
                    </TableCell>
                    {showInvoice && (
                      <TableCell className="py-1.5 font-mono text-[10px] align-top">
                        {entry.invoice_number || entry.correction_number || "—"}
                      </TableCell>
                    )}
                    <TableCell className="py-1.5 align-top">
                      <div>{entry.customer_name || entry.category || entry.description || entry.type}</div>
                      {entry.voided && (
                        <Badge variant="destructive" className="text-[9px] px-1 py-0">VOIDED</Badge>
                      )}
                    </TableCell>
                    {(showTraceability || hasTraceability) && (
                      <TableCell className="py-1.5 align-top">
                        {showBreakdown ? (
                          <div className="space-y-0.5 text-[10px]">
                            {entry.sale_total !== undefined && (
                              <div className="text-muted-foreground">
                                Sale: {fmt(entry.sale_total)}
                              </div>
                            )}
                            {entry.payment_received !== undefined && entry.payment_received !== entry.sale_total && (
                              <div className="text-green-700">
                                Received: {fmt(entry.payment_received)}
                              </div>
                            )}
                            {hasCreditAdj && (
                              <div className="text-amber-700 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Credit: {fmt(entry.customer_credit)}
                              </div>
                            )}
                            {hasOtherPay && (
                              <div className="text-blue-600">
                                + {entry.other_payments.map(p => `${p.mode.toUpperCase()} ${fmt(p.amount)}`).join(", ")}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="py-1.5 text-right font-mono align-top">
                      <div className={hasCreditAdj ? "font-semibold" : ""}>
                        {fmt(entry.amount)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {hasTraceability && (
            <div className="p-2 bg-muted/30 border-t text-[10px] text-muted-foreground">
              <span className="font-medium">Note:</span> When a customer pays more than the sale total, the excess is credited to their store account.
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Summary Row with Drill-Down
function SummaryRow({ label, amount, count, entries, showDrillDown = true }) {
  return (
    <div className="flex justify-between items-start py-1.5">
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {showDrillDown && entries && entries.length > 0 && (
          <TransactionList entries={entries} />
        )}
      </div>
      <div className="text-right">
        <div className="font-mono text-sm">{fmt(amount)}</div>
        {count > 0 && <div className="text-[10px] text-muted-foreground">{count} txn</div>}
      </div>
    </div>
  );
}

// ==========================================
// CASH RECONCILIATION CARD (Full Featured)
// ==========================================
function CashReconciliationCard({ data, loading }) {
  const [showDetails, setShowDetails] = useState(false);
  
  if (loading) {
    return (
      <Card className="border-green-200" data-testid="cash-recon-card-loading">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Banknote className="w-5 h-5 text-green-600" />
            CASH
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded"></div>
            <div className="h-6 bg-muted rounded w-3/4"></div>
            <div className="h-6 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { opening, inflows, outflows, voided, summary } = data;
  
  const statusIcon = summary.status === "balanced" 
    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
    : summary.status === "pending"
    ? <Clock className="w-5 h-5 text-yellow-600" />
    : summary.status === "excess"
    ? <AlertTriangle className="w-5 h-5 text-amber-600" />
    : <XCircle className="w-5 h-5 text-red-600" />;

  return (
    <Card className="border-green-200" data-testid="cash-recon-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Banknote className="w-5 h-5 text-green-600" />
            CASH
          </CardTitle>
          <div className="flex items-center gap-2">
            {statusIcon}
            <Badge 
              variant={summary.status === "balanced" ? "default" : summary.status === "pending" ? "secondary" : "destructive"}
              data-testid="cash-status-badge"
            >
              {summary.status === "balanced" ? "Balanced" : summary.status === "pending" ? "Pending Close" : `Variance: ${fmt(summary.variance)}`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
          <div className="bg-blue-50 rounded p-2">
            <div className="text-xs text-blue-600">Opening</div>
            <div className="font-mono font-semibold" data-testid="cash-opening">{fmt(summary.opening)}</div>
          </div>
          <div className="bg-green-50 rounded p-2">
            <div className="text-xs text-green-600">+ Inflows</div>
            <div className="font-mono font-semibold text-green-700" data-testid="cash-total-inflow">{fmt(summary.total_inflow)}</div>
          </div>
          <div className="bg-red-50 rounded p-2">
            <div className="text-xs text-red-600">- Outflows</div>
            <div className="font-mono font-semibold text-red-700" data-testid="cash-total-outflow">{fmt(summary.total_outflow)}</div>
          </div>
          <div className="bg-purple-50 rounded p-2">
            <div className="text-xs text-purple-600">= Expected</div>
            <div className="font-mono font-semibold text-purple-700" data-testid="cash-expected-closing">{fmt(summary.expected_closing)}</div>
          </div>
        </div>

        {/* Actual vs Expected */}
        {summary.actual_closing !== null && (
          <div className="flex justify-between items-center p-3 bg-muted rounded" data-testid="cash-actual-section">
            <div>
              <div className="text-sm font-medium">Actual Closing</div>
              <div className="text-xs text-muted-foreground">Counted cash in drawer</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-lg font-semibold" data-testid="cash-actual-closing">{fmt(summary.actual_closing)}</div>
              {summary.variance !== 0 && (
                <div 
                  className={`text-sm font-mono ${summary.variance > 0 ? "text-amber-600" : "text-red-600"}`}
                  data-testid="cash-variance"
                >
                  {summary.variance > 0 ? "+" : ""}{fmt(summary.variance)} variance
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detailed Breakdown Toggle */}
        <Collapsible open={showDetails} onOpenChange={setShowDetails}>
          <CollapsibleTrigger 
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer w-full justify-center py-2 border-t"
            data-testid="cash-details-toggle"
          >
            {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showDetails ? "Hide Details" : "Show Calculation Details"}
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            {/* Opening */}
            <div className="text-sm">
              <div className="font-medium mb-1">Opening Balance</div>
              <div className="flex justify-between text-muted-foreground">
                <span>From: {opening.source === "previous_close" ? `Previous close (${opening.from_date})` : opening.source}</span>
                <span className="font-mono">{fmt(opening.amount)}</span>
              </div>
            </div>

            {/* Inflows */}
            <div className="text-sm">
              <div className="font-medium mb-1 text-green-700 flex items-center gap-1">
                <ArrowDownCircle className="w-4 h-4" /> Inflows
              </div>
              <SummaryRow label="Cash Sales" amount={inflows.sales.total} count={inflows.sales.count} entries={inflows.sales.entries} />
              <SummaryRow label="Credit Collections" amount={inflows.collections.total} count={inflows.collections.count} entries={inflows.collections.entries} />
              <SummaryRow label="Other Incomes" amount={inflows.incomes.total} count={inflows.incomes.count} entries={inflows.incomes.entries} />
              {inflows.corrections?.total > 0 && (
                <SummaryRow label="Correction Inflows" amount={inflows.corrections.total} count={inflows.corrections.count} entries={inflows.corrections.entries} />
              )}
            </div>

            {/* Outflows */}
            <div className="text-sm">
              <div className="font-medium mb-1 text-red-700 flex items-center gap-1">
                <ArrowUpCircle className="w-4 h-4" /> Outflows
              </div>
              <SummaryRow label="Expenses" amount={outflows.expenses.total} count={outflows.expenses.count} entries={outflows.expenses.entries} />
              <SummaryRow label="Refunds" amount={outflows.refunds.total} count={outflows.refunds.count} entries={outflows.refunds.entries} />
              {outflows.corrections?.total > 0 && (
                <SummaryRow label="Correction Outflows" amount={outflows.corrections.total} count={outflows.corrections.count} entries={outflows.corrections.entries} />
              )}
            </div>

            {/* Voided */}
            {voided && voided.count > 0 && (
              <div className="text-sm border-t pt-2">
                <div className="font-medium mb-1 text-gray-500 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Voided/Corrected (Excluded from calculation)
                </div>
                <div className="bg-gray-100 rounded p-2">
                  <SummaryRow 
                    label="Voided Entries" 
                    amount={voided.total} 
                    count={voided.count} 
                    entries={voided.entries} 
                  />
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ==========================================
// UPI/CARD MOVEMENT CARD
// ==========================================
function MovementCard({ title, icon: Icon, iconColor, data, loading, testIdPrefix }) {
  const [showDetails, setShowDetails] = useState(false);
  
  if (loading) {
    return (
      <Card data-testid={`${testIdPrefix}-recon-card-loading`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { inflows, outflows, voided, summary } = data;

  return (
    <Card data-testid={`${testIdPrefix}-recon-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          {title}
        </CardTitle>
        <CardDescription className="text-xs">{data.note}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-green-50 rounded p-2">
            <div className="text-xs text-green-600">Inflows</div>
            <div className="font-mono font-semibold text-green-700" data-testid={`${testIdPrefix}-total-inflow`}>{fmt(summary.total_inflow)}</div>
          </div>
          <div className="bg-red-50 rounded p-2">
            <div className="text-xs text-red-600">Outflows</div>
            <div className="font-mono font-semibold text-red-700" data-testid={`${testIdPrefix}-total-outflow`}>{fmt(summary.total_outflow)}</div>
          </div>
          <div className="bg-blue-50 rounded p-2">
            <div className="text-xs text-blue-600">Net Movement</div>
            <div className="font-mono font-semibold" data-testid={`${testIdPrefix}-net-movement`}>{fmt(summary.net_movement)}</div>
          </div>
        </div>

        {/* Details Toggle */}
        <Collapsible open={showDetails} onOpenChange={setShowDetails}>
          <CollapsibleTrigger 
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer w-full justify-center py-2 border-t"
            data-testid={`${testIdPrefix}-details-toggle`}
          >
            {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showDetails ? "Hide Details" : "Show Details"}
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            <div className="text-sm">
              <div className="font-medium mb-1 text-green-700 flex items-center gap-1">
                <ArrowDownCircle className="w-4 h-4" /> Inflows
              </div>
              <SummaryRow label="Sales" amount={inflows.sales.total} count={inflows.sales.count} entries={inflows.sales.entries} />
              <SummaryRow label="Collections" amount={inflows.collections.total} count={inflows.collections.count} entries={inflows.collections.entries} />
              <SummaryRow label="Other Incomes" amount={inflows.incomes.total} count={inflows.incomes.count} entries={inflows.incomes.entries} />
            </div>
            <div className="text-sm">
              <div className="font-medium mb-1 text-red-700 flex items-center gap-1">
                <ArrowUpCircle className="w-4 h-4" /> Outflows
              </div>
              <SummaryRow label="Expenses" amount={outflows.expenses.total} count={outflows.expenses.count} entries={outflows.expenses.entries} />
              <SummaryRow label="Refunds" amount={outflows.refunds.total} count={outflows.refunds.count} entries={outflows.refunds.entries} />
            </div>
            {voided && voided.count > 0 && (
              <div className="text-sm border-t pt-2">
                <div className="font-medium mb-1 text-gray-500 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Voided (Excluded)
                </div>
                <SummaryRow label="Voided Entries" amount={voided.total} count={voided.count} entries={voided.entries} />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ==========================================
// CUSTOMER CREDIT RECONCILIATION CARD
// ==========================================
function CreditReconciliationCard({ data, loading }) {
  const [showDetails, setShowDetails] = useState(false);
  
  if (loading) {
    return (
      <Card className="border-purple-200" data-testid="credit-recon-card-loading">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-purple-600" />
            CUSTOMER CREDIT
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { increases, decreases, summary } = data;

  return (
    <Card className="border-purple-200" data-testid="credit-recon-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="w-5 h-5 text-purple-600" />
          CUSTOMER CREDIT
        </CardTitle>
        <CardDescription className="text-xs">{data.note}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-red-50 rounded p-2">
            <div className="text-xs text-red-600">+ Credit Sales</div>
            <div className="font-mono font-semibold text-red-700" data-testid="credit-sales-total">{fmt(summary.credit_sales)}</div>
          </div>
          <div className="bg-green-50 rounded p-2">
            <div className="text-xs text-green-600">- Collections</div>
            <div className="font-mono font-semibold text-green-700" data-testid="credit-collections-total">{fmt(summary.payments_received)}</div>
          </div>
          <div className="bg-blue-50 rounded p-2">
            <div className="text-xs text-blue-600">Net Change</div>
            <div 
              className={`font-mono font-semibold ${summary.net_change >= 0 ? "text-red-700" : "text-green-700"}`} 
              data-testid="credit-net-change"
            >
              {summary.net_change >= 0 ? "+" : ""}{fmt(summary.net_change)}
            </div>
          </div>
        </div>

        {decreases.payments_received?.by_mode && (
          <div className="text-xs text-center text-muted-foreground">
            Collections by mode: 
            {Object.entries(decreases.payments_received.by_mode)
              .filter(([_, v]) => v > 0)
              .map(([mode, amt]) => ` ${mode.toUpperCase()}: ${fmt(amt)}`)
              .join(" | ") || " None"}
          </div>
        )}

        {/* Details Toggle */}
        <Collapsible open={showDetails} onOpenChange={setShowDetails}>
          <CollapsibleTrigger 
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer w-full justify-center py-2 border-t"
            data-testid="credit-details-toggle"
          >
            {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showDetails ? "Hide Details" : "Show Details"}
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            <div className="text-sm">
              <div className="font-medium mb-1 text-red-700 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" /> Increases (Receivables Up)
              </div>
              <SummaryRow 
                label="Credit Sales" 
                amount={increases.credit_sales.total} 
                count={increases.credit_sales.count} 
                entries={increases.credit_sales.entries}
              />
            </div>
            <div className="text-sm">
              <div className="font-medium mb-1 text-green-700 flex items-center gap-1">
                <TrendingDown className="w-4 h-4" /> Decreases (Receivables Down)
              </div>
              <SummaryRow 
                label="Payments Received" 
                amount={decreases.payments_received.total} 
                count={decreases.payments_received.count} 
                entries={decreases.payments_received.entries}
              />
              <SummaryRow 
                label="Returns to Credit" 
                amount={decreases.returns_credits.total} 
                count={decreases.returns_credits.count} 
                entries={decreases.returns_credits.entries}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ==========================================
// MAIN DRAWER RECONCILIATION TAB
// ==========================================
export function DrawerReconciliationTab({ filter, currentShop }) {
  const { shopId, isAllShops, dateFrom, dateTo } = filter;
  const [loading, setLoading] = useState(false);
  const [cashData, setCashData] = useState(null);
  const [upiData, setUpiData] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [creditData, setCreditData] = useState(null);

  const sameDay = dateFrom && dateTo && dateFrom === dateTo;

  const fetchData = async () => {
    if (!shopId || isAllShops || !sameDay) return;
    
    setLoading(true);
    try {
      const [cashRes, upiRes, cardRes, creditRes] = await Promise.all([
        api.get(`/shop-reconciliation/cash?shop_id=${shopId}&date=${dateFrom}`),
        api.get(`/shop-reconciliation/upi?shop_id=${shopId}&date=${dateFrom}`),
        api.get(`/shop-reconciliation/card?shop_id=${shopId}&date=${dateFrom}`),
        api.get(`/shop-reconciliation/credit?shop_id=${shopId}&date=${dateFrom}`),
      ]);
      
      setCashData(cashRes.data);
      setUpiData(upiRes.data);
      setCardData(cardRes.data);
      setCreditData(creditRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setCashData(null);
      setUpiData(null);
      setCardData(null);
      setCreditData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shopId && !isAllShops && sameDay) {
      fetchData();
    } else {
      setCashData(null);
      setUpiData(null);
      setCardData(null);
      setCreditData(null);
    }
  }, [shopId, dateFrom, dateTo, isAllShops, sameDay]);

  // Display states for invalid selections
  if (isAllShops || !shopId) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="drawer-needs-shop">
        <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <div className="font-medium mb-1">Select a Shop</div>
        Drawer reconciliation is per-shop. Pick a specific shop above to view today&apos;s financial reconciliation.
      </Card>
    );
  }

  if (!sameDay) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="drawer-needs-single-day">
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <div className="font-medium mb-1">Select a Single Day</div>
        Drawer reconciliation works on a single day at a time. Set <span className="font-semibold">From</span> and <span className="font-semibold">To</span> to the same date (or click <span className="font-semibold">Today</span> / <span className="font-semibold">Yesterday</span>) to see the drawer.
      </Card>
    );
  }

  const shopName = cashData?.shop_name || currentShop?.name || "";
  const displayDate = new Date(dateFrom).toLocaleDateString("en-IN", { 
    weekday: "long", year: "numeric", month: "long", day: "numeric" 
  });

  return (
    <div className="space-y-4" data-testid="drawer-reconciliation">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold uppercase tracking-wide" data-testid="drawer-title">
            Daily Drawer Reconciliation
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {shopName} · {displayDate}
          </p>
        </div>
        <Button 
          onClick={fetchData} 
          disabled={loading} 
          variant="outline" 
          size="sm"
          data-testid="drawer-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Reconciliation Cards Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <CashReconciliationCard data={cashData} loading={loading} />
        <CreditReconciliationCard data={creditData} loading={loading} />
        <MovementCard 
          title="UPI" 
          icon={Smartphone} 
          iconColor="text-blue-600" 
          data={upiData} 
          loading={loading}
          testIdPrefix="upi"
        />
        <MovementCard 
          title="CARD" 
          icon={CreditCard} 
          iconColor="text-orange-600" 
          data={cardData} 
          loading={loading}
          testIdPrefix="card"
        />
      </div>

      {/* Voided Corrections Section */}
      {cashData?.voided?.count > 0 && (
        <Card className="border-amber-200" data-testid="voided-corrections-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
              <FileText className="w-5 h-5" />
              Financial Corrections Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              {cashData.voided.count} voided/corrected transaction(s) excluded from calculations.
            </p>
            <div className="max-h-32 overflow-auto border rounded text-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs py-1">Type</TableHead>
                    <TableHead className="text-xs py-1">Category</TableHead>
                    <TableHead className="text-xs py-1">Reason</TableHead>
                    <TableHead className="text-xs py-1 text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cashData.voided.entries.map((entry, idx) => (
                    <TableRow key={entry.source_id || idx} className="bg-amber-50">
                      <TableCell className="py-1">{entry.type}</TableCell>
                      <TableCell className="py-1">{entry.category}</TableCell>
                      <TableCell className="py-1 max-w-[200px] truncate">{entry.voided_reason || "—"}</TableCell>
                      <TableCell className="py-1 text-right font-mono">{fmt(entry.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-4 bg-muted/30">
        <div className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-semibold uppercase tracking-wide">How it works:</span> Each drawer shows every payment movement at this shop today.
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li><span className="font-semibold">CASH</span>: Expected Closing = Opening + Money In − Money Out. Actual closing is counted in the Opening/Closing tab via the denominations table.</li>
            <li><span className="font-semibold">UPI/CARD</span>: Daily movement only (settlements are external via bank/processor).</li>
            <li><span className="font-semibold">CREDIT</span>: Customer receivables movement (credit sales vs collections).</li>
          </ul>
          <div className="mt-2">
            Click <span className="font-semibold text-blue-600">&quot;Show Details&quot;</span> on any card to drill down into individual transactions.
          </div>
        </div>
      </Card>
    </div>
  );
}
